import { Link, Navigate, useParams } from 'react-router-dom';
import { useTenantList } from '../../api/tenants';
import { EmptyState, ErrorState, SkeletonStats } from '../../components/states';
import { PageHeader } from '../../components/ui';

/** The API's slug rule; anything else is not a tenant and needs no request. */
const SLUG = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * /:slug — a tenant by its URL handle, for bookmarks and links from before
 * tenants had a page of their own. Resolves the slug and sends the browser to
 * /tenant/<id>, where the page lives. Later this moves to
 * <slug>.balsamsolutions.com; for now the path.
 */
export function TenantPage() {
  const { slug = '' } = useParams();
  const valid = SLUG.test(slug);
  const tenants = useTenantList({ slug, limit: 1 }, { enabled: valid });

  if (!valid) return <NotFound slug={slug} />;
  if (tenants.isPending || tenants.isPlaceholderData) {
    return (
      <>
        <PageHeader eyebrow="Tenant" title={slug} />
        <SkeletonStats count={4} />
      </>
    );
  }
  if (tenants.isError) {
    return (
      <>
        <PageHeader eyebrow="Tenant" title={slug} />
        <ErrorState
          error={tenants.error}
          onRetry={() => tenants.refetch()}
          retrying={tenants.isFetching}
        />
      </>
    );
  }
  const tenant = tenants.data.data[0] ?? null;
  if (!tenant) return <NotFound slug={slug} />;
  return <Navigate to={`/tenant/${tenant.id}`} replace />;
}

function NotFound({ slug }: { slug: string }) {
  return (
    <>
      <PageHeader eyebrow="Tenant" title="No tenant here" />
      <EmptyState
        title="Nothing at this address"
        body={
          <>
            There is no tenant with the slug <code>{slug}</code>.
          </>
        }
        action={<Link to="/staff">Back to the console</Link>}
      />
    </>
  );
}
